import React, { useState, useEffect } from "react";
import { 
  Plus, Edit2, Trash2, Shield, Briefcase, Users, Calculator, 
  Archive, CreditCard, TrendingUp, Lock, Check, X, ShieldAlert,
  Globe, Smartphone, HelpCircle, FileText, Home, ShoppingCart,
  BarChart3, Clipboard, Box, Receipt, Truck, Package, UserPlus,
  AlertTriangle, Bell, Settings, ChevronDown, ChevronRight
} from "lucide-react";
import authAxios from "../../api/authAxios";
import { usePermissions } from "../../context/PermissionsContext";

// Exact sidebar items matching the navigation
const SIDEBAR_SECTIONS = [
  {
    category: "Main Navigation",
    items: [
      { label: "Owner Dashboard", permCode: "view_owner_dashboard", icon: Home },
      { label: "Employee Dashboard", permCode: "view_employee_dashboard", icon: Home },
      { label: "POS Billing", permCode: "view_billing", icon: ShoppingCart },
      { label: "Invoice History", permCode: "export_billing", icon: FileText },
      { label: "Reports", permCode: "view_reports", icon: BarChart3 },
      { label: "Purchase Requests", permCode: "view_purchase", icon: Clipboard },
    ]
  },
  {
    category: "Inventory",
    items: [
      { label: "All Products", permCode: "view_inventory", icon: Box },
      { label: "Smart Purchase Bills", permCode: "create_purchase", icon: Receipt },
      { label: "Stock Inward", permCode: "create_inventory", icon: Truck },
      { label: "Suppliers", permCode: "view_vendors", icon: Users },
      { label: "Categories", permCode: "edit_inventory", icon: Package },
    ]
  },
  {
    category: "Customers",
    items: [
      { label: "View Customers", permCode: "view_customers", icon: Users },
      { label: "Manage Customers", permCode: "edit_customers", icon: UserPlus },
      { label: "Export Customers", permCode: "export_customers", icon: FileText },
    ]
  },
  {
    category: "Employees / HRM",
    items: [
      { label: "All Employees", permCode: "view_employees", icon: Users },
      { label: "Add Employee", permCode: "create_employees", icon: UserPlus },
      { label: "Attendance Reports", permCode: "view_attendance", icon: Clipboard },
      { label: "Leave Approvals", permCode: "approve_attendance", icon: AlertTriangle },
      { label: "Payroll Management", permCode: "view_payroll", icon: CreditCard },
      { label: "Employee Performance", permCode: "view_sales", icon: TrendingUp },
    ]
  },
  {
    category: "Settings & Support",
    items: [
      { label: "Profile Settings", permCode: "view_settings", icon: Settings },
      { label: "Notifications", permCode: "view_notifications", icon: Bell },
      { label: "Subscription", permCode: "view_payments", icon: CreditCard },
      { label: "Support", permCode: "view_services", icon: HelpCircle },
    ]
  },
  {
    category: "Quick Access",
    items: [
      { label: "Check In / Out", permCode: "quick_checkin", icon: Check },
      { label: "POS Billing", permCode: "quick_billing", icon: ShoppingCart },
      { label: "Invoice History", permCode: "quick_invoices", icon: FileText },
      { label: "Customers", permCode: "quick_customers", icon: Users },
      { label: "Manage Employees", permCode: "quick_employees", icon: UserPlus },
      { label: "Collections", permCode: "quick_collections", icon: CreditCard },
      { label: "Notifications", permCode: "quick_notifications", icon: Bell },
    ]
  }
];

const POPULAR_ICONS = [
  { name: "Shield", icon: Shield },
  { name: "Briefcase", icon: Briefcase },
  { name: "Users", icon: Users },
  { name: "Calculator", icon: Calculator },
  { name: "Archive", icon: Archive },
  { name: "CreditCard", icon: CreditCard },
  { name: "TrendingUp", icon: TrendingUp },
  { name: "Lock", icon: Lock }
];

const THEME_COLORS = [
  { name: "Blue", value: "#3B82F6" },
  { name: "Green", value: "#10B981" },
  { name: "Red", value: "#EF4444" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Orange", value: "#F59E0B" },
  { name: "Cyan", value: "#06B6D4" },
  { name: "Rose", value: "#F43F5E" }
];

export default function RolesPermissionsManagement() {
  const { togglePermission, permissions, ALL_PERMISSIONS } = usePermissions();
  
  // State
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [auditLogs, setAuditLogs] = useState([]);
  const [matrixData, setMatrixData] = useState({});
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  // Role Form State
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    icon: "Shield",
    theme_color: "#3B82F6",
    is_active: true,
    login_access: true,
    web_access: true,
    mobile_access: true
  });

  // Fetch Roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/api/users/roles/");
      setRoles(res.data);
      if (res.data.length > 0 && !selectedRole) {
        setSelectedRole(res.data[0]);
      }
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Permission Matrix for all roles
  const fetchMatrix = async () => {
    setLoadingMatrix(true);
    try {
      const res = await authAxios.get("/api/users/roles/matrix/");
      setMatrixData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMatrix(false);
    }
  };

  // Fetch Audit Logs
  const fetchAuditLogs = async () => {
    try {
      const res = await authAxios.get("/api/users/roles/audit-logs/");
      setAuditLogs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchMatrix();
    fetchAuditLogs();
  }, []);

  // Update selected role state if roles list updates
  useEffect(() => {
    if (selectedRole) {
      const updated = roles.find(r => r.id === selectedRole.id);
      if (updated) setSelectedRole(updated);
    }
  }, [roles]);

  const handleOpenAddRole = () => {
    setEditingRole(null);
    setRoleForm({
      name: "",
      description: "",
      icon: "Shield",
      theme_color: "#3B82F6",
      is_active: true,
      login_access: true,
      web_access: true,
      mobile_access: true
    });
    setShowRoleModal(true);
  };

  const handleOpenEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || "",
      icon: role.icon || "Shield",
      theme_color: role.theme_color || "#3B82F6",
      is_active: role.is_active,
      login_access: role.login_access,
      web_access: role.web_access,
      mobile_access: role.mobile_access
    });
    setShowRoleModal(true);
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!roleForm.name.trim()) {
      setError("Role name is required");
      return;
    }

    try {
      const payload = {
        name: roleForm.name.toUpperCase().replace(/\s+/g, "_"),
        description: roleForm.description,
        icon: roleForm.icon,
        theme_color: roleForm.theme_color,
        is_active: roleForm.is_active,
        login_access: roleForm.login_access,
        web_access: roleForm.web_access,
        mobile_access: roleForm.mobile_access
      };

      if (editingRole) {
        await authAxios.patch(`/api/users/roles/${editingRole.id}/`, payload);
        setSuccessMsg("Role updated successfully!");
      } else {
        const response = await authAxios.post("/api/users/roles/", payload);
        setSuccessMsg("Role created successfully!");
        if (response.data) setSelectedRole(response.data);
      }
      
      setShowRoleModal(false);
      fetchRoles();
      fetchMatrix();
      fetchAuditLogs();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to save role");
    }
  };

  const handleDeleteRole = async (role) => {
    const systemRoles = ["SUPERADMIN", "SUPER_ADMIN", "OWNER", "SALES_EXECUTIVE"];
    if (systemRoles.includes(role.name.toUpperCase())) {
      alert("System roles cannot be deleted!");
      return;
    }

    if (window.confirm(`Are you sure you want to delete the role '${role.name}'?`)) {
      try {
        await authAxios.delete(`/api/users/roles/${role.id}/`);
        setSuccessMsg("Role deleted successfully!");
        if (selectedRole?.id === role.id) {
          setSelectedRole(null);
        }
        fetchRoles();
        fetchMatrix();
        fetchAuditLogs();
        setTimeout(() => setSuccessMsg(""), 3000);
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.detail || "Failed to delete role");
      }
    }
  };

  const handleTogglePermission = async (roleName, permCode) => {
    const roleUpper = roleName.toUpperCase();
    const currentEnabled = matrixData[roleName]?.includes(permCode) || false;
    const nextState = !currentEnabled;
    
    try {
      console.log(`[Admin RBAC Logs] Toggling permission '${permCode}' for role ${roleUpper} to ${nextState}`);
      // Write to Django database
      await authAxios.post("/api/users/roles/matrix/", {
        role: roleUpper,
        permission: permCode,
        enabled: nextState
      });
      
      // Update UI state only after successful API call
      setMatrixData(prev => {
        const currentList = prev[roleName] || [];
        const updatedList = nextState
          ? [...currentList, permCode]
          : currentList.filter(c => c !== permCode);
        return {
          ...prev,
          [roleName]: updatedList
        };
      });
      
      fetchAuditLogs();
      setSuccessMsg(`Permission '${permCode}' updated successfully to ${nextState ? 'ON' : 'OFF'}.`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error(err);
      setError(`Failed to synchronize permission update for '${permCode}'`);
      setTimeout(() => setError(""), 4000);
    }
  };

  // Render Lucide Icon dynamically helper
  const renderIcon = (iconName, className) => {
    const iconObj = POPULAR_ICONS.find(i => i.name === iconName);
    const IconComponent = iconObj ? iconObj.icon : Shield;
    return <IconComponent className={className} />;
  };

  return (
    <div className="space-y-8 p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6 border-slate-200">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Roles & Permissions Management</h1>
          <p className="text-slate-500 mt-1">Configure user roles, metadata, and access permission matrix in real-time.</p>
        </div>
        <button
          onClick={handleOpenAddRole}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md shadow-blue-600/10 text-sm"
        >
          <Plus className="w-4 h-4" /> Add Custom Role
        </button>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-100 rounded-lg text-rose-700 text-sm flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-rose-500 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-700 text-sm flex items-center gap-2">
          <Check className="w-5 h-5 text-emerald-500 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Grid of Roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {roles.map(role => {
          const isSelected = selectedRole?.id === role.id;
          const isSystem = ["SUPERADMIN", "SUPER_ADMIN", "OWNER", "SALES_EXECUTIVE"].includes(role.name.toUpperCase());
          return (
            <div
              key={role.id}
              onClick={() => setSelectedRole(role)}
              style={{ borderLeftColor: role.theme_color || "#3B82F6" }}
              className={`p-5 rounded-xl border-l-[5px] bg-white border border-slate-200 shadow-sm cursor-pointer transition-all hover:shadow-md relative overflow-hidden flex flex-col justify-between ${
                isSelected ? "ring-2 ring-blue-500 shadow-md transform -translate-y-0.5" : ""
              }`}
            >
              <div>
                <div className="flex justify-between items-start">
                  <div className="p-2.5 rounded-lg text-white" style={{ backgroundColor: role.theme_color || "#3B82F6" }}>
                    {renderIcon(role.icon, "w-5 h-5")}
                  </div>
                  <div className="flex gap-1.5">
                    {role.web_access && (
                      <span className="p-1 bg-blue-50 text-blue-600 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-0.5" title="Web Access Enabled">
                        <Globe className="w-3 h-3" /> WEB
                      </span>
                    )}
                    {role.mobile_access && (
                      <span className="p-1 bg-purple-50 text-purple-600 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-0.5" title="Mobile Access Enabled">
                        <Smartphone className="w-3 h-3" /> MOB
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-bold text-slate-800 mt-4 tracking-tight">
                  {role.name.replace(/_/g, " ")}
                </h3>
                
                <p className="text-slate-500 text-sm mt-1.5 line-clamp-2">
                  {role.description || "No description provided."}
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100 flex justify-between items-center">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  role.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                }`}>
                  {role.is_active ? "Active" : "Inactive"}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEditRole(role);
                    }}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {!isSystem && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRole(role);
                      }}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Permission Matrix - Sidebar Items */}
      {selectedRole && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
            <div>
              <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                <Lock className="w-5 h-5 text-slate-600" />
                Permissions for Role: <span className="text-blue-600">{selectedRole.name.replace(/_/g, " ")}</span>
              </h2>
              <p className="text-slate-500 text-sm mt-0.5">Toggle sidebar feature access. Changes save automatically.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-blue-500"></span>
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Live Permission Editor</span>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {SIDEBAR_SECTIONS.map(section => {
              const isSuperAdminRole = selectedRole.name.toUpperCase() === "SUPERADMIN" || selectedRole.name.toUpperCase() === "SUPER_ADMIN";
              const enabledCount = section.items.filter(item => 
                matrixData[selectedRole.name]?.includes(item.permCode) || isSuperAdminRole
              ).length;

              return (
                <div key={section.category} className="border border-slate-200 rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <div className="bg-slate-50 px-5 py-3.5 flex items-center justify-between border-b border-slate-200">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">{section.category}</h3>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      enabledCount === section.items.length 
                        ? "bg-emerald-50 text-emerald-700" 
                        : enabledCount === 0 
                          ? "bg-slate-100 text-slate-500" 
                          : "bg-amber-50 text-amber-700"
                    }`}>
                      {enabledCount}/{section.items.length} enabled
                    </span>
                  </div>

                  {/* Items */}
                  <div className="divide-y divide-slate-100">
                    {section.items.map(item => {
                      const IconComp = item.icon;
                      const isChecked = matrixData[selectedRole.name]?.includes(item.permCode) || isSuperAdminRole;
                      return (
                        <div 
                          key={item.permCode} 
                          className={`flex items-center justify-between px-5 py-3.5 transition-colors ${
                            isChecked ? "bg-white" : "bg-slate-50/40"
                          } hover:bg-blue-50/30`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isChecked ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
                              <IconComp className="w-4 h-4" />
                            </div>
                            <div>
                              <span className={`text-sm font-semibold ${isChecked ? "text-slate-800" : "text-slate-500"}`}>
                                {item.label}
                              </span>
                              <p className="text-[11px] text-slate-400 font-mono">{item.permCode}</p>
                            </div>
                          </div>

                          {/* Toggle Switch */}
                          <button
                            disabled={isSuperAdminRole}
                            onClick={() => handleTogglePermission(selectedRole.name, item.permCode)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                              isSuperAdminRole ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                            } ${isChecked ? "bg-blue-600" : "bg-slate-300"}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                              isChecked ? "translate-x-6" : "translate-x-1"
                            }`} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50/50">
          <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            RBAC Audit Logs
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">Real-time log trail tracking privilege changes and role modifications.</p>
        </div>

        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="p-4 font-bold text-slate-700 text-xs uppercase tracking-wider">Timestamp</th>
                <th className="p-4 font-bold text-slate-700 text-xs uppercase tracking-wider">Changed By</th>
                <th className="p-4 font-bold text-slate-700 text-xs uppercase tracking-wider">Event Action</th>
                <th className="p-4 font-bold text-slate-700 text-xs uppercase tracking-wider">Affected Role</th>
                <th className="p-4 font-bold text-slate-700 text-xs uppercase tracking-wider">Event Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-400">No audit logs found.</td>
                </tr>
              ) : (
                auditLogs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50/40">
                    <td className="p-4 text-slate-500 whitespace-nowrap">
                      {new Date(log.created_at || log.timestamp).toLocaleString()}
                    </td>
                    <td className="p-4 text-slate-700 font-medium">
                      {log.changed_by_name || "System"} ({log.changed_by_phone || "Admin"})
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                        log.action === "CREATE_ROLE" ? "bg-blue-50 text-blue-700" :
                        log.action === "DELETE_ROLE" ? "bg-rose-50 text-rose-700" :
                        log.action === "TOGGLE_PERMISSION" ? "bg-emerald-50 text-emerald-700" :
                        "bg-amber-50 text-amber-700"
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="p-4 text-slate-800 font-semibold">{log.role_name}</td>
                    <td className="p-4 text-slate-600">{log.description}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Add/Edit Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden transform transition-all">
            <div className="p-6 border-b border-slate-150 flex justify-between items-center bg-slate-50">
              <h3 className="text-lg font-bold text-slate-800">
                {editingRole ? "Modify Role Details" : "Create New Custom Role"}
              </h3>
              <button
                onClick={() => setShowRoleModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveRole} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Role Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. MANAGER"
                  disabled={editingRole} // Name is immutable once created
                  value={roleForm.name}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Description</label>
                <textarea
                  placeholder="Role description and business capabilities..."
                  value={roleForm.description}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Select Icon</label>
                  <select
                    value={roleForm.icon}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    {POPULAR_ICONS.map(i => (
                      <option key={i.name} value={i.name}>{i.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Theme Color</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={roleForm.theme_color}
                      onChange={(e) => setRoleForm(prev => ({ ...prev, theme_color: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                    >
                      {THEME_COLORS.map(c => (
                        <option key={c.value} value={c.value}>{c.name}</option>
                      ))}
                    </select>
                    <div className="h-8 w-8 rounded-full border border-slate-200 shadow-sm shrink-0" style={{ backgroundColor: roleForm.theme_color }} />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <span className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Access Control Configurations</span>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Role Status (Active)</label>
                  <input
                    type="checkbox"
                    checked={roleForm.is_active}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="h-4.5 w-4.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Allow login access</label>
                  <input
                    type="checkbox"
                    checked={roleForm.login_access}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, login_access: e.target.checked }))}
                    className="h-4.5 w-4.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Web Dashboard Access</label>
                  <input
                    type="checkbox"
                    checked={roleForm.web_access}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, web_access: e.target.checked }))}
                    className="h-4.5 w-4.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Mobile App Access</label>
                  <input
                    type="checkbox"
                    checked={roleForm.mobile_access}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, mobile_access: e.target.checked }))}
                    className="h-4.5 w-4.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowRoleModal(false)}
                  className="flex-1 py-2 bg-white hover:bg-slate-50 border rounded-lg text-sm font-semibold text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-md shadow-blue-600/10"
                >
                  {editingRole ? "Save Changes" : "Create Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
